import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { getOpsChat, postOpsChatAction } from '../../../api/twoDays/ops.api';
import { Bubbles, SendButton, composerClass } from '../../../components/TwoDays/OpsChat';
import { JOB_LABEL, cn, initials } from '../../../components/TwoDays/model/derive';
import { KST, formatClock, formatDay } from '../../../components/TwoDays/model/time';
import useMedia, { MOBILE } from '../../../components/TwoDays/useMedia';

/**
 * Ops › Messages — 팀 › 팀원 1:1 메신저 (운영진 쪽). 5초마다 다시 읽는다.
 *   왼쪽 목록(데스크톱 340 · 태블릿 280): All · Waiting · Resolved 밑줄 탭, 팀별로 접고 펴기, 명단 전체
 *   팀 안 순서: 운영진 답 기다림 → 안 읽음 → 최근 → 대화 없음
 *   오른쪽 대화: 머리(이름 · 팀 · 직군 · 상태 알약 · Resolve) · 말풍선 · 답장(Enter 보냄, Shift+Enter 줄바꿈, 쓰는 동안 팀원에게 입력 중)
 *   모바일: 목록 → 줄을 누르면 대화(← 뒤로), 상태는 이름 아래 글자로
 */

const POLL_MS = 5_000;
const STATUS = {
  none: ['New', 'bg-grayscale-gray01 text-text-tertiary', 'bg-grayscale-gray05'],
  waiting: ['Waiting for ops', 'bg-functional-warning01 text-functional-warning04', 'bg-functional-warning03'],
  open: ['Open', 'bg-surface-green10 text-green-deep04', 'bg-green-deep02'],
  resolved: ['Resolved', 'bg-grayscale-gray01 text-text-tertiary', 'bg-grayscale-gray05'],
};
const clock = (iso) => (iso ? formatClock(new Date(iso), KST) : '');
const dayClock = (iso, now) => {
  if (!iso) return '';
  const d = new Date(iso);
  return formatDay(d, KST) === formatDay(now, KST) ? clock(iso) : `${formatDay(d, KST)} ${clock(iso)}`;
};
const minsAgo = (iso, now) => Math.max(0, Math.round((now - new Date(iso)) / 60_000));
const statusOf = (th) => (th.messages.length ? th.status : 'none');

const Avatar = ({ name }) => (
  <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[11px] font-medium text-green-deep04'>{initials(name)}</span>
);
const Unread = ({ n }) =>
  n > 0 ? (
    <span className='box-border flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-functional-failed03 px-[5px] text-[11px] leading-[18px] text-grayscale-gray00'>{n}</span>
  ) : null;

/* ── 목록 ───────────────────────────────────────────── */

const MemberRow = ({ m, selected, now, onPick }) => {
  const th = m.thread;
  const s0 = statusOf(th);
  const last = [...th.messages].reverse().find((x) => x.sender !== 'system');
  const lastM = [...th.messages].reverse().find((x) => x.sender === 'member');
  const wait = s0 === 'waiting' && lastM ? minsAgo(lastM.createdAt, now) : null;
  const snip = wait != null ? `Waiting ${wait}m · ${last?.body ?? ''}` : last ? `${last.sender === 'ops' ? 'You: ' : ''}${last.body}` : s0 === 'resolved' ? 'Resolved' : 'No messages yet';
  return (
    <li>
      <div
        role='button'
        tabIndex={0}
        aria-current={selected || undefined}
        onClick={() => onPick(m.id)}
        onKeyDown={(e) => e.key === 'Enter' && onPick(m.id)}
        className={cn('grid min-h-[60px] cursor-pointer grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-2.5 px-4 py-2', selected ? 'bg-surface-green10 shadow-[inset_3px_0_0_#1dc37b]' : 'hover:bg-green-deep03/[0.04]')}
      >
        <Avatar name={m.name} />
        <span className='flex min-w-0 flex-col gap-0.5'>
          <span className={cn('flex items-center gap-1.5 text-[14px] leading-[140%]', th.unread ? 'font-medium' : 'font-normal')}>
            <span className='min-w-0 truncate text-text-primary'>{m.name}</span>
            <span className='shrink-0 text-[12px] font-normal text-text-secondary'>
              {JOB_LABEL[m.job] ?? m.job}
              {m.leader ? ' · Leader' : m.staff ? ' · Staff' : ''}
            </span>
          </span>
          <span className={cn('truncate text-[12px] leading-[140%]', wait != null ? 'text-functional-warning04' : last ? 'text-text-secondary' : 'text-text-tertiary')}>{snip}</span>
        </span>
        <span className='flex flex-col items-end gap-1'>
          <span className={cn('whitespace-nowrap text-[11px] leading-[140%] tabular-nums', th.unread ? 'text-functional-failed03' : 'text-text-tertiary')}>{last ? dayClock(last.createdAt, now) : ''}</span>
          <Unread n={th.unread} />
        </span>
      </div>
    </li>
  );
};

const TeamGroup = ({ team, members, selectedId, now, onPick }) => {
  const [open, setOpen] = useState(true);
  const unread = members.reduce((n, m) => n + m.thread.unread, 0);
  return (
    <li className='flex flex-col'>
      <button type='button' aria-expanded={open} onClick={() => setOpen((v) => !v)} className='flex h-10 items-center gap-2 px-4 text-left text-[12px] font-medium text-text-secondary'>
        <ChevronIcon className={cn('h-3.5 w-3.5 transition-transform', !open && '-rotate-90')} />
        <span className='flex-1'>Team {team.name}</span>
        <Unread n={unread} />
      </button>
      {open && (
        <ul className='flex flex-col'>
          {members.map((m) => (
            <MemberRow key={m.id} m={m} selected={selectedId === m.id} now={now} onPick={onPick} />
          ))}
        </ul>
      )}
    </li>
  );
};

/* ── 대화 ───────────────────────────────────────────── */

const Thread = ({ team, m, onSend, onResolve, onTyping, onBack, mobile }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const typingRef = useRef(false);
  const th = m.thread;
  const s0 = statusOf(th);
  const [label, chip, dotCls] = STATUS[s0];
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [th.messages.length, m.id]);
  useEffect(() => setText(''), [m.id]);
  useEffect(() => {
    const on = !!text.trim();
    if (on === typingRef.current) return;
    typingRef.current = on;
    onTyping(on);
  }, [text, onTyping]);
  useEffect(() => () => { if (typingRef.current) onTyping(false); }, [onTyping]);

  const send = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    const ok = await onSend(body);
    setBusy(false);
    if (ok) setText('');
  };
  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      send();
    }
  };
  const first = m.name.split(' ')[0];
  return (
    <section className='flex min-h-0 min-w-0 flex-1 flex-col'>
      <header className='flex min-h-[64px] items-center gap-3 border-b border-green-deep03/[0.08] px-4 py-2.5'>
        {mobile && (
          <button type='button' aria-label='Back to messages' onClick={onBack} className='-ml-2.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-green-deep03/[0.06]'>
            <ChevronIcon className='h-5 w-5 rotate-90 text-icon-primary' />
          </button>
        )}
        <Avatar name={m.name} />
        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <span className='text-[15px] font-medium leading-[140%] text-text-primary'>{m.name}</span>
          <span className='truncate text-[12px] leading-[140%] text-text-secondary'>
            {mobile ? `${label} · ` : ''}Team {team.name} · {JOB_LABEL[m.job] ?? m.job}
          </span>
        </div>
        {!mobile && (
          <span className={cn('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[12px] font-medium', chip)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', dotCls)} />
            {label}
          </span>
        )}
        {['open', 'waiting'].includes(s0) && (
          <button type='button' onClick={onResolve} className='h-9 shrink-0 rounded-full bg-green-deep03/[0.08] px-3.5 text-[13px] font-medium text-text-primary hover:bg-green-deep03/[0.14]'>
            Resolve
          </button>
        )}
      </header>
      <div className='flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto bg-[color-mix(in_srgb,#009e4f_2%,#fff)] px-5 py-4'>
        {th.messages.length === 0 ? (
          <div className='m-auto flex max-w-[320px] flex-col items-center gap-1.5 text-center'>
            <span className='text-[15px] font-medium leading-[140%] text-text-primary'>No messages yet</span>
            <span className='text-[13px] leading-[150%] text-text-secondary'>Start a conversation with {first}. They’ll see it in the Ops chat on the workboard.</span>
          </div>
        ) : (
          <ul className='flex flex-col gap-2.5'>
            <Bubbles messages={th.messages} mine='ops' who={() => m.name} max='max-w-[72%]' />
          </ul>
        )}
        <div ref={endRef} />
      </div>
      <div className='flex items-end gap-2 border-t border-green-deep03/[0.08] px-4 py-3'>
        <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={1} maxLength={2000} placeholder={`Reply to ${first}`} aria-label='Reply' className={cn(composerClass, 'max-h-[140px]')} />
        <SendButton disabled={!text.trim() || busy} onClick={send} />
      </div>
    </section>
  );
};

/* ── 화면 ───────────────────────────────────────────── */

const OpsMessages = () => {
  const { project, toast, setChatUnread } = useOutletContext();
  const projectId = project?.id ?? null;
  const mobile = useMedia(MOBILE);
  const tablet = useMedia('(max-width: 1179px)');
  const [board, setBoard] = useState(null);
  const [tab, setTab] = useState('all');
  const [selId, setSelId] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const pending = useRef(0);

  const load = useCallback(() => {
    if (projectId == null) return Promise.resolve();
    return getOpsChat(projectId)
      .then((b) => {
        if (pending.current > 0) return;
        setBoard(b);
        setNow(new Date());
      })
      .catch(() => {});
  }, [projectId]);
  useEffect(() => {
    setBoard(null);
    setSelId(null);
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const act = useCallback(
    async (memberId, body) => {
      pending.current += 1;
      try {
        const b = await postOpsChatAction(projectId, memberId, body);
        setBoard(b);
        setNow(new Date());
        return true;
      } catch (e) {
        if (body.type !== 'typing') toast?.({ type: 'error', state: 'white', title: e?.response?.data?.msg || 'Couldn’t send. Try again.' });
        return false;
      } finally {
        pending.current = Math.max(0, pending.current - 1);
      }
    },
    [projectId, toast],
  );

  const current = useMemo(() => {
    if (!board || selId == null) return null;
    for (const team of board.teams) {
      const m = team.members.find((x) => x.id === selId);
      if (m) return { team, m };
    }
    return null;
  }, [board, selId]);

  useEffect(() => {
    if (current?.m.thread.unread > 0) act(current.m.id, { type: 'read' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.m.id, current?.m.thread.unread]);
  useEffect(() => {
    if (board) setChatUnread?.(board.unread);
  }, [board, setChatUnread]);
  const onTyping = useCallback((on) => current && act(current.m.id, { type: 'typing', on }), [current, act]);

  if (!project) return <p className='py-10 text-center text-[14px] text-text-secondary'>Pick a project above.</p>;
  if (!board) return <p className='py-10 text-center text-[14px] text-text-secondary'>Loading…</p>;

  const filter = (m) => (tab === 'waiting' ? statusOf(m.thread) === 'waiting' : tab === 'resolved' ? statusOf(m.thread) === 'resolved' : true);
  const rank = (m) => (statusOf(m.thread) === 'waiting' ? 0 : m.thread.unread ? 1 : m.thread.messages.length ? 2 : 3);
  const groups = board.teams
    .map((t) => ({ team: t, members: t.members.filter(filter).sort((a, b) => rank(a) - rank(b) || new Date(b.thread.lastAt ?? 0) - new Date(a.thread.lastAt ?? 0)) }))
    .filter((g) => g.members.length);
  const sum = board.unread ? `${board.unread} unread · ${board.waiting} waiting` : board.waiting ? `${board.waiting} waiting` : 'All caught up';
  const pick = (id) => {
    setSelId(id);
    setShowThread(true);
  };

  const list = (
    <aside className={cn('flex min-h-0 min-w-0 flex-col', mobile ? 'w-full' : tablet ? 'w-[280px] shrink-0 border-r border-green-deep03/[0.08]' : 'w-[340px] shrink-0 border-r border-green-deep03/[0.08]')}>
      <div role='tablist' className='flex gap-1 border-b border-green-deep03/[0.08] px-3 pt-2.5'>
        {[
          ['all', 'All'],
          ['waiting', `Waiting · ${board.waiting}`],
          ['resolved', 'Resolved'],
        ].map(([k, label]) => (
          <button key={k} type='button' role='tab' aria-selected={tab === k} onClick={() => setTab(k)} className={cn('h-10 px-2.5 text-[14px]', tab === k ? 'font-medium text-text-primary shadow-[inset_0_-2px_0_#1dc37b]' : 'text-text-secondary hover:text-text-primary')}>
            {label}
          </button>
        ))}
      </div>
      <ul className='flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden py-1.5'>
        {groups.length === 0 ? (
          <li className='m-4 rounded-xl border border-dashed border-green-deep03/[0.24] px-4 py-5 text-center text-[13px] leading-[150%] text-text-secondary'>
            {tab === 'waiting' ? 'No one is waiting for a reply.' : tab === 'resolved' ? 'No resolved conversations yet.' : 'No members yet.'}
          </li>
        ) : (
          groups.map((g) => <TeamGroup key={g.team.id} team={g.team} members={g.members} selectedId={selId} now={now} onPick={pick} />)
        )}
      </ul>
    </aside>
  );
  const thread = current ? (
    <Thread team={current.team} m={current.m} mobile={mobile} onBack={() => setShowThread(false)} onSend={(body) => act(current.m.id, { type: 'send', body })} onResolve={() => act(current.m.id, { type: 'resolve' })} onTyping={onTyping} />
  ) : (
    <section className='m-auto flex flex-col items-center gap-1.5 p-6 text-center'>
      <span className='text-[15px] font-medium leading-[140%] text-text-primary'>Pick a member</span>
      <span className='text-[13px] leading-[150%] text-text-secondary'>Messages are grouped by team, then by member.</span>
    </section>
  );

  return (
    <div className={cn('flex flex-col', mobile ? 'gap-4' : 'gap-6')}>
      <div className='flex flex-col gap-1.5'>
        <span className='text-[13px] leading-[140%] text-text-secondary'>Ops console · {sum}</span>
        <h1 className={cn('font-medium leading-[120%] tracking-[-0.01em] text-text-primary', mobile ? 'text-[24px]' : 'text-[28px]')}>Messages</h1>
      </div>
      <div className={cn('flex overflow-hidden rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4', mobile ? 'h-[calc(100vh-180px)]' : 'h-[640px]')}>
        {mobile ? (showThread && current ? thread : list) : (
          <>
            {list}
            {thread}
          </>
        )}
      </div>
    </div>
  );
};

export default OpsMessages;
